import { useOrderFormStore } from '@/lib/store/order-form-store';
import { useOutcomePrices } from '@/lib/store/prices-store';
import { EMarketAction } from '@/lib/types/market-action';
import type { MarketItem } from '@/lib/types/markets/market';
import type { PriceAlertSide } from '../types';

export function outcomeCentsForSide(outcome: MarketItem | undefined, side: PriceAlertSide): number {
  if (!outcome?.outcomePrices || outcome.outcomePrices.length === 0) return 0;
  const raw = side === 'YES' ? outcome.outcomePrices[0] : outcome.outcomePrices[1];
  const num = Number.parseFloat(raw ?? '0');
  if (Number.isNaN(num)) return 0;
  return Math.round(num * 100);
}

export interface OutcomeLiveCents {
  yesCents: number;
  noCents: number;
}

// Tradable = trading is open: status is ACTIVE (or unset) — excludes PAUSED, AWAITING_RESOLUTION, RESOLVED.
export function isOutcomeTradable(outcome: MarketItem): boolean {
  return outcome.status === undefined || outcome.status === 'ACTIVE';
}

// Mirrors OrderForm's price display logic so PriceAlerts and OrderForm stay in sync.
export function useOutcomeLiveCents(outcome: MarketItem | undefined): OutcomeLiveCents {
  const livePrices = useOutcomePrices(outcome?.id ?? '');
  const action = useOrderFormStore.use.action();

  const isBuy = action === EMarketAction.BUY;

  const yesFraction = isBuy ? livePrices?.bestAsk : livePrices?.bestBid;
  const noBaseFraction = isBuy ? livePrices?.bestBid : livePrices?.bestAsk;

  const yesCents = typeof yesFraction === 'number' ? Math.round(yesFraction * 100) : 0;
  const noCents = typeof noBaseFraction === 'number' ? Math.round((1 - noBaseFraction) * 100) : 0;

  return { yesCents, noCents };
}
